import { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle,
  IconButton, InputAdornment,
  Stack, TextField, Typography,
  Table, TableBody, TableCell,
  TableHead, TableRow, Paper, Tooltip, Avatar,
  FormControl, Select, MenuItem, InputLabel, Divider
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import ScrollableTableContainer from "components/table/ScrollableTableContainer";
import TableActionButton from "components/table/TableActionButton";
import TableActions from "components/table/TableActions";
import TableActionCell from "components/table/TableActionCell";
import ClickableTableRow, { stopRowClickPropagation } from "components/table/ClickableTableRow";
import { useNavigate } from "react-router-dom";
import TableTimeChips from "components/table/TableTimeChips";
import TableStatusChip from "components/table/TableStatusChip";
import { sequenceChipSx } from "components/table/tableChipStyles";
import SearchIcon from "@mui/icons-material/Search";
import ShopIcon from "@mui/icons-material/Storefront";
import CategoryIcon from "@mui/icons-material/Category";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";

import api, { IMG_BASE_URL } from "../../utils/api";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import CategoryFilteredByStoreForm from "./CategoryFilteredByStoreForm";
import { useToast } from "contexts/ToastContext";

function formatTime(t) {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr || "00";
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

export default function CategoryFilteredByStoreList() {
  const navigate = useNavigate();
  const { showToast, showApiError } = useToast();
  const [categories, setCategories] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  

  const [formModal, setFormModal] = useState({ open: false, id: null });
  const [delDialog, setDelDialog] = useState({ open: false, id: null, name: "" });
  const [deleting, setDeleting] = useState(false);

  const loadStores = useCallback(async () => {
    try {
      const res = await api.get("/store/list");
      const data = res.data?.data || [];
      const sorted = [...data].sort((a, b) => b.store_id - a.store_id);
      setStores(sorted);
      if (sorted.length > 0) {
        setSelectedStore(sorted[0].store_id);
      }
    } catch (err) {
      console.error("Failed to fetch stores:", err);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    if (!selectedStore) return;
    setLoading(true);
    try {
      const res = await api.get("/product/category/list", {
        params: { store_id: selectedStore }
      });
      const data = res.data?.data || [];
      setCategories(data);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    setFiltered(
      !q ? categories
        : categories.filter((c) =>
          [c.name_en, c.name_ar].join(" ").toLowerCase().includes(q)
        )
    );
  }, [search, categories]);

  const handleToggleStatus = async (cat) => {
    setTogglingId(cat.id);
    try {
      await api.patch(`/product/category/toggle-status/${cat.id}`);
      setCategories((prev) =>
        prev.map((r) => r.id === cat.id ? { ...r, status: !r.status } : r)
      );
    } catch (err) {
      showApiError(err, "Failed to update status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/product/category/delete/${delDialog.id}`);
      setCategories((prev) => prev.filter((c) => c.id !== delDialog.id));
      setDelDialog({ open: false, id: null, name: "" });
    } catch (err) {
      showApiError(err, "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const getStoreName = () => stores.find(s => s.store_id === selectedStore)?.title_en || "Selected Store";
  const isStoreInactive = stores.find(s => s.store_id === selectedStore)?.status === false;

  const handleViewProducts = (cat) => {
    const storeName = getStoreName();
    navigate(
      `/product/list?store_id=${selectedStore}&category_id=${cat.id}&category_name=${encodeURIComponent(cat.name_en)}&store_name=${encodeURIComponent(storeName)}`
    );
  };

  const COLS = ["#", "Image", "Name", "Arabic Name", "Seq", "Times", "Status", "Actions"];

  return (
    <Box sx={{ p: 1 }}>
      <PageHeader
        title="Master Categories"
        subtitle={
          selectedStore
            ? `${getStoreName()} — manage categories for the selected store.`
            : "Select a store to view and manage its categories."
        }
        count={filtered.length}
        titleExtra={
          selectedStore && isStoreInactive ? (
            <Chip label="Store Inactive" size="small" color="error" variant="outlined" sx={{ fontWeight: 600 }} />
          ) : null
        }
        actionLabel="Add Category"
        actionIcon={AddIcon}
        onAction={() => setFormModal({ open: true, id: null })}
        actionDisabled={!selectedStore}
      />

      {/* Control Bar */}
      <MainCard sx={{ mb: 3, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} content={false}>
        <Box sx={{ p: 2.5, display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="store-select-label">Active Store Filter</InputLabel>
            <Select
              labelId="store-select-label"
              value={selectedStore}
              label="Active Store Filter"
              onChange={(e) => setSelectedStore(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <ShopIcon fontSize="small" color="action" />
                </InputAdornment>
              }
              sx={{ borderRadius: 2 }}
            >
              {stores.map((s) => (
                <MenuItem key={s.store_id} value={s.store_id}>
                  {s.title_en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search category names…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
            sx={{ maxWidth: 400, flex: 1 }}
          />
          
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={() => { loadStores(); loadCategories(); }} 
              sx={{ 
                bgcolor: 'grey.50', 
                border: '1px solid', 
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </MainCard>

      {/* Table Data */}
      <MainCard contentSX={{ p: 0 }} sx={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', borderRadius: 4, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 12 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 12, color: "text.secondary" }}>
            <Typography variant="h5" fontWeight={600}>No Categories Found</Typography>
            <Typography variant="body2">Try adjusting your filters or add a new category.</Typography>
          </Box>
        ) : (
          <ScrollableTableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {COLS.map((col) => (
                    <TableCell 
                      key={col}
                      sx={{ 
                        bgcolor: 'grey.50', 
                        fontWeight: 700, 
                        fontSize: '0.72rem', 
                        textTransform: 'uppercase', 
                        color: 'text.secondary',
                        py: 2,
                        letterSpacing: "0.05em"
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((c, idx) => (
                  <ClickableTableRow
                    key={c.id}
                    sx={{ verticalAlign: "middle" }}
                    onClick={() => handleViewProducts(c)}
                  >
                    <TableCell sx={{ color: 'text.disabled', fontWeight: 600 }}>{idx + 1}</TableCell>
                    <TableCell>
                      <Avatar 
                        src={c.Doc?.imageUrl || (c.Doc?.attachment_url ? `${IMG_BASE_URL}/${c.Doc.attachment_url}` : null)} 
                        variant="rounded"
                        sx={{ width: 48, height: 48, bgcolor: 'primary.lighter', border: "1px solid", borderColor: "divider" }}
                      >
                        {c.name_en?.[0]}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{c.name_en}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ direction: 'rtl' }}>{c.name_ar || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={c.sequence || 0} size="small" sx={sequenceChipSx} />
                    </TableCell>
                    <TableCell>
                      <TableTimeChips times={c.times} formatTime={formatTime} />
                    </TableCell>
                    <TableCell>
                      <TableStatusChip
                        active={!!c.status}
                        loading={togglingId === c.id}
                        loadingLabel="Syncing..."
                        onClick={stopRowClickPropagation(() => togglingId !== c.id && handleToggleStatus(c))}
                      />
                    </TableCell>
                    <TableActionCell>
                      <TableActions>
                        <TableActionButton
                          action="view"
                          title="View Products"
                          onClick={() => handleViewProducts(c)}
                        />
                        <TableActionButton
                          action="edit"
                          onClick={() => setFormModal({ open: true, id: c.id })}
                        />
                        <TableActionButton
                          action="delete"
                          onClick={() => setDelDialog({ open: true, id: c.id, name: c.name_en })}
                        />
                      </TableActions>
                    </TableActionCell>
                  </ClickableTableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
        )}
      </MainCard>

      {/* Category Form Modal */}
      <CategoryFilteredByStoreForm 
        open={formModal.open}
        categoryId={formModal.id}
        storeId={selectedStore}
        storeName={getStoreName()}
        onSave={loadCategories}
        onClose={() => setFormModal({ open: false, id: null })}
      />

      {/* Delete Confirmation */}
      <Dialog
        open={delDialog.open}
        onClose={() => !deleting && setDelDialog({ open: false, id: null, name: "" })}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to remove <strong>{delDialog.name}</strong>. This will affect all associated products and cannot be reversed.
          </Typography>
        </DialogContent>
        <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" color="inherit" onClick={() => setDelDialog({ open: false, id: null, name: "" })} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{
              bgcolor: '#b71c1c',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#8e0000' },
              '&.Mui-disabled': { bgcolor: 'rgba(183, 28, 28, 0.45)', color: '#fff' }
            }}
          >
            {deleting ? "Removing..." : "Delete Category"}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
